
import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Story } from '@/utils/storiesData';
import StoryCard from '@/components/story/StoryCard';
import StoriesFilter from '@/components/story/StoriesFilter';
import FeaturedStoriesCarousel from '@/components/story/FeaturedStoriesCarousel';
import StoriesGrid from '@/components/story/StoriesGrid';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { ArrowRight } from 'lucide-react';
import contentfulService from '@/services/contentfulService';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';

const Stories = () => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { resolvedTheme } = useTheme();
  const [contentfulConnected, setContentfulConnected] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Test Contentful connection on component mount
    contentfulService.testConnection()
      .then(connected => {
        setContentfulConnected(connected);
        if (!connected) {
          toast({
            title: "Contentful Connection Issue",
            description: "Unable to connect to Contentful. Using mock data instead.",
            variant: "destructive"
          });
        }
      });
  }, []);

  // Fetch all stories using React Query
  const { data: stories = [], isLoading, error } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories,
  });

  const filteredStories = stories.filter(story => {
    const matchesCategory = filter === 'all' || story.category.toLowerCase() === filter.toLowerCase();
    const matchesSearch = searchQuery === '' || 
      story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      story.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      story.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  // Extract unique categories from stories
  const uniqueCategories = [...new Set(stories.map(story => story.category.toLowerCase()))];
  
  // Group stories by category
  const storiesByCategory = Array.from(new Set(stories.map(story => story.category)))
    .map(category => {
      return {
        name: category,
        stories: stories.filter(story => story.category === category)
      };
    });

  const featuredStories = filteredStories.filter(story => story.featured);
  const regularStories = filteredStories.filter(story => !story.featured);

  const clearFilters = () => {
    setFilter('all');
    setSearchQuery('');
  };

  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const subTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const bgColor = resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background';

  if (isLoading) {
    return (
      <Layout>
        <div className={cn("py-8", bgColor)}>
          <div className="article-container">
            <Skeleton className="h-12 w-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-80 w-full" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className={cn("py-16 text-center", bgColor)}>
          <h2 className={cn("text-2xl mb-4", textColor)}>Error loading stories</h2>
          <p className={cn("mb-4", subTextColor)}>
            There was a problem connecting to the content service. Please try again later.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={cn("py-8", bgColor)}>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 xl:px-16">
          {!contentfulConnected && (
            <div className="mb-6 px-4 py-3 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-md">
              <p className="text-sm">
                Using mock data. To connect to Contentful, please ensure you have created content types and entries.
              </p>
            </div>
          )}
          
          <div className="sticky top-16 z-40 bg-background backdrop-blur-md border-b border-border/40 pb-3 pt-2 md:top-20 md:pb-4 md:z-20">
            <StoriesFilter 
              categories={uniqueCategories}
              activeFilter={filter}
              onFilterChange={setFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>

          {featuredStories.length > 0 && filter === 'all' && searchQuery === '' && (
            <div className="mb-16">
              <h2 className={cn("hodinkee-heading mb-6 font-chroma font-normal", textColor)}>Featured Stories</h2>
              <FeaturedStoriesCarousel stories={featuredStories} />
            </div>
          )}

          {filter === 'all' && searchQuery === '' ? (
            <div className="space-y-16">
              {storiesByCategory.map((category, index) => (
                category.stories.length > 0 && (
                  <div key={category.name} className="section-container pt-0">
                    <div className="flex items-center mb-8">
                      <h2 className={cn("hodinkee-heading font-chroma font-normal", textColor)}>{category.name}</h2>
                      <Separator className="flex-1 ml-4" />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6 lg:gap-8">
                      {category.stories.slice(0, 4).map((story) => (
                        <StoryCard 
                          key={story.id} 
                          story={story} 
                          featured={false}
                        />
                      ))}
                    </div>
                    
                    {category.stories.length > 4 && (
                      <div className="mt-6 text-right">
                        <button 
                          onClick={() => setFilter(category.name.toLowerCase())} 
                          className={cn("text-sm font-medium hover:underline flex items-center ml-auto", textColor)}
                        >
                          View all {category.name} stories
                          <ArrowRight size={14} className="ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          ) : (
            <StoriesGrid 
              stories={filteredStories}
              filter={filter}
              onClearFilters={clearFilters}
              searchQuery={searchQuery}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Stories;
